import React, {useRef, useState} from 'react'
import Popover from "@mui/material/Popover"
import Box from "@mui/material/Box"
import Radio from "@mui/material/Radio"
import TuneIcon from '@mui/icons-material/Tune'
import classnames from "classnames"
import {NumericFormat} from 'react-number-format'

import CustomCurrencyInput from "./CustomCurrencyInput"
import './PaymentAdjustmentPopover.scss'

// What a forced Monthly Payment adjusts. UI identifiers only — they are not an
// API contract (see paymentAdjustmentRequest in DealContext).
export const PAYMENT_ADJUSTMENT = {
    DRIVE_OFF: 'drive_off',
    COST: 'cost',
    PROFIT: 'profit',
}

const OPTIONS = [
    {id: PAYMENT_ADJUSTMENT.DRIVE_OFF, label: 'Total Drive Off', title: 'Total Drive Off (Amount Due at Signing)', valueKey: 'totalDriveOff'},
    {id: PAYMENT_ADJUSTMENT.COST, label: 'Cost', valueKey: 'cost'},
    {id: PAYMENT_ADJUSTMENT.PROFIT, label: 'Profit', valueKey: 'profit'},
]

// Card width (.PaymentAdjustmentPopover) plus Popover's viewport margin.
const ROOM_BESIDE = 488 + 16

// The Cost Calculator's corner anchoring, mirrored: the payment field sits at
// the page's right edge, so the card opens down and to its left. Without room
// there (phones), it opens above or below the field, whichever has more space.
const placement = (anchorEl) => {
    const rect = anchorEl?.getBoundingClientRect?.()
    if (!rect || rect.left >= ROOM_BESIDE) {
        return {anchorOrigin: {vertical: 'bottom', horizontal: 'left'}, transformOrigin: {vertical: 'top', horizontal: 'right'}}
    }
    return rect.top > window.innerHeight / 2
        ? {anchorOrigin: {vertical: 'top', horizontal: 'right'}, transformOrigin: {vertical: 'bottom', horizontal: 'right'}}
        : {anchorOrigin: {vertical: 'bottom', horizontal: 'right'}, transformOrigin: {vertical: 'top', horizontal: 'right'}}
}

/**
 * Opened from the Monthly Payment field: the user types the payment they want
 * and picks what absorbs the change. Shows each option's current quote value;
 * TDO (today's behaviour) is preselected.
 *
 * Purely presentational: it reports (payment, choice) through onConfirm, or
 * onClose when dismissed, and owns no pricing.
 */
export const PaymentAdjustmentPopover = (props) => {
    const {
        anchorEl,
        currentPayment,
        cost,
        profit,
        totalDriveOff,
        onConfirm,
        onClose,
    } = props

    const [payment, setPayment] = useState(currentPayment ?? '')
    const [selected, setSelected] = useState(PAYMENT_ADJUSTMENT.DRIVE_OFF)
    // A ref, not state: two Enters in one tick must still settle only once.
    const isSettledRef = useRef(false)
    const values = {cost, profit, totalDriveOff}
    // Empty is a valid submission: it returns the deal to its normal Amount Due at Signing.
    const canConfirm = payment === '' || Number(payment) > 0

    const settle = (report) => {
        if (isSettledRef.current) return
        isSettledRef.current = true
        report()
    }

    // Cancel is a click away or Escape — both land here via Popover's onClose.
    const dismiss = () => settle(() => onClose && onClose())
    const confirm = () => {
        if (!canConfirm) return
        settle(() => onConfirm && onConfirm(payment, selected))
    }

    const handleKeyDown = (e) => {
        if (e.key !== 'Enter') return
        // Keep the Deal page's document-level Enter from re-running Calculate.
        e.stopPropagation()
        // A focused button activates itself (Cancel must not confirm).
        if (e.target.tagName === 'BUTTON') return
        e.preventDefault()
        confirm()
    }

    return (
        <Popover
            onClick={(e) => e.stopPropagation()}
            open={true}
            anchorEl={anchorEl}
            onClose={dismiss}
            // The field takes focus back itself; MUI's restore would reopen it.
            disableRestoreFocus
            {...placement(anchorEl)}
            slotProps={{paper: {className: 'PaymentAdjustmentPopover-paper'}}}
        >
            <Box
                className={'PaymentAdjustmentPopover'}
                data-testid={'payment-adjustment-popover'}
                tabIndex={-1}
                onKeyDown={handleKeyDown}
            >
                <div className={'pa-card-header'}>
                    <TuneIcon/>
                    <h2>Force Payment</h2>
                </div>

                {/* One grid for both halves, so New Payment shares the TDO row
                    (see .pa-card-body). */}
                <div className={'pa-card-body'}>
                    <label className={'pa-field-label pa-payment-label'} htmlFor={'payment-adjustment-new-payment'}>
                        New Payment
                    </label>
                    <div className={'pa-slot pa-payment-slot'}>
                        <div className={'pa-input'}>
                            <span className={'prefix'}>$</span>
                            <CustomCurrencyInput
                                id={'payment-adjustment-new-payment'}
                                value={payment}
                                onChange={(value) => setPayment(value)}
                                allowNegative={false}
                                allowBlank={true}
                                autoFocus
                                // Typing replaces the current payment; Delete clears it.
                                onFocus={(e) => e.target.select()}
                                // Chrome would remember and autofill typed payments here.
                                autoComplete={'off'}
                                InputProps={{}}
                            />
                        </div>
                    </div>

                    <div className={'pa-divider'}/>

                    <div className={'pa-field-label pa-options-label'} id={'payment-adjustment-group-label'}>Adjust</div>
                    <div className={'pa-options'} role={'radiogroup'} aria-labelledby={'payment-adjustment-group-label'}>
                        {
                            OPTIONS.map((option) => {
                                const isSelected = selected === option.id

                                return (
                                    <div
                                        key={option.id}
                                        className={classnames('pa-row', {selected: isSelected})}
                                        title={option.title}
                                        // Picking a target keeps the caret in New Payment.
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => setSelected(option.id)}
                                    >
                                        <Radio
                                            checked={isSelected}
                                            value={option.id}
                                            name={'payment-adjustment'}
                                            disableRipple
                                            onChange={() => setSelected(option.id)}
                                            inputProps={{'aria-label': option.label}}
                                        />
                                        <span className={'pa-label'}>{option.label}</span>
                                        <span className={'pa-value'}>
                                            <span className={'prefix'}>$</span>
                                            <NumericFormat
                                                value={Number(values[option.valueKey]) || 0}
                                                displayType={'text'}
                                                thousandSeparator
                                                decimalScale={2}
                                                fixedDecimalScale
                                            />
                                        </span>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>

                <div className={'pa-card-footer'}>
                    <button type={'button'} className={'pa-submit'} onClick={confirm} disabled={!canConfirm}>
                        Calculate
                    </button>
                </div>
            </Box>
        </Popover>
    )
}
